import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthToken } from '../lib/authToken';
import * as api from './api';

export const zenKeys = {
  bootstrap: ['zen', 'bootstrap'] as const,
  audit: ['zen', 'audit'] as const,
  order: (id: string) => ['zen', 'order', id] as const,
  ledger: (id: string) => ['zen', 'ledger', id] as const,
  portal: ['zen', 'portal'] as const,
};

export function useZenBootstrap() {
  const token = useAuthToken();
  return useQuery({
    queryKey: zenKeys.bootstrap,
    queryFn: ({ signal }) => api.fetchBootstrap(signal),
    enabled: Boolean(token),
    refetchInterval: 10_000,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
    staleTime: 2_000,
  });
}

export function useZenAuditLog() {
  return useQuery({
    queryKey: zenKeys.audit,
    queryFn: ({ signal }) => api.fetchAuditLog(signal),
    staleTime: 30_000,
  });
}

export function useWorkOrder(id: string) {
  return useQuery({
    queryKey: zenKeys.order(id),
    queryFn: ({ signal }) => api.fetchWorkOrder(id, signal),
    enabled: Boolean(id),
    staleTime: 5_000,
  });
}

export function useZenMutation<TVariables>(
  mutationFn: (variables: TVariables) => Promise<unknown>,
  orderId?: string,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: zenKeys.bootstrap });
      if (orderId) await client.invalidateQueries({ queryKey: zenKeys.order(orderId) });
    },
  });
}

export function usePortalBoard() {
  return useQuery({
    queryKey: zenKeys.portal,
    queryFn: ({ signal }) => api.fetchPortalBoard(signal),
    refetchInterval: 30_000,
    staleTime: 5_000,
  });
}
